import { TrendingDown, TrendingUp, Minus, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  /** Current-window value. */
  current: number | null | undefined;
  /** Previous-window value (same duration, immediately before). */
  previous: number | null | undefined;
  /** Optional className for spacing/alignment. */
  className?: string;
};

/**
 * TrendBadge — renders a compact "+N %" / "-N %" pill with an arrow,
 * computed from `current` vs `previous`. Special cases :
 *
 *   - previous == 0 && current > 0   → "Nouveau" (no division by zero)
 *   - previous == 0 && current == 0  → "—"
 *   - previous > 0  && current == 0  → "-100 %" (with destructive tone)
 *
 * The bigger the move, the more saturated the colour gets so heavy
 * activity stands out without ever using hardcoded greys.
 */
export function TrendBadge({ current, previous, className }: Props) {
  const cur = current ?? 0;
  const prev = previous ?? 0;

  // Both zero → nothing to compare.
  if (cur === 0 && prev === 0) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium",
          "bg-surface-muted text-foreground-subtle",
          className
        )}
      >
        <Minus className="size-3" />
        —
      </span>
    );
  }

  // No baseline → flag as new ; emphasis depends on volume.
  if (prev === 0 && cur > 0) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium",
          "bg-success/15 text-success ring-1 ring-success/30",
          className
        )}
        title={`+${cur} (aucune activité dans la fenêtre précédente)`}
      >
        <Sparkles className="size-3" />
        Nouveau
      </span>
    );
  }

  // Both > 0 → real percentage.
  const pct = ((cur - prev) / prev) * 100;
  const up = pct > 0;
  const flat = Math.abs(pct) < 0.5;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;

  // 3-tier tone : flat = neutral, mild = subtle colour, strong = ring.
  let tone: string;
  const mag = Math.abs(pct);
  if (flat) {
    tone = "bg-surface-muted text-foreground-muted";
  } else if (up) {
    tone =
      mag >= 50
        ? "bg-success/20 text-success ring-1 ring-success/40"
        : "bg-success/10 text-success";
  } else {
    tone =
      mag >= 50
        ? "bg-destructive/20 text-destructive ring-1 ring-destructive/40"
        : "bg-destructive/10 text-destructive";
  }

  // Compact formatting : 0 decimals when |pct| ≥ 10, 1 decimal otherwise.
  // Cap displayed value at +999 % / -99 % to keep the badge slim.
  const sign = up ? "+" : "";
  let display: string;
  if (mag >= 999) display = up ? "+999 %" : "-99 %";
  else if (mag >= 10) display = `${sign}${pct.toFixed(0)} %`;
  else display = `${sign}${pct.toFixed(1)} %`;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
        tone,
        className
      )}
      title={`${cur} vs ${prev} (fenêtre précédente)`}
    >
      <Icon className="size-3" />
      {display}
    </span>
  );
}
